// src/components/ProductList.tsx
import React, { useState, useMemo } from 'react';
import { Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { useCanvaAuth } from '../contexts/CanvaAuthContext2.tsx';
import DowngradeConfirmationModal  from './DowngradeConfirmationModal.js';

type ProductListProps = {
  billingPeriod?: 'monthly' | 'annual';
};

/** Temporary: tier headline + italic line under the plan button */
const HIDE_PRODUCT_DESCRIPTION_AND_POST_CTA = true;

/** Temporary: hide free (tier 1.0 / price "free") product card from the list */
const TEMP_HIDE_FREE_PRODUCT = false;

function isFreeTierProduct(product: { price?: string; tier?: string | number }): boolean {
  if (product.price === 'free') return true;
  const t = parseFloat(String(product.tier));
  return !Number.isNaN(t) && Math.abs(t - 1.0) < 0.01;
}

const ProductList: React.FC<ProductListProps> = ({ billingPeriod = 'monthly' }) => {
  const { canvaAuthenticated, setCanvaAuthenticated, token } = useCanvaAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | false>(false);
  const [downgradeFlyout, setDowngradeFlyout] = useState<any>(false);
  
  // Helper function to convert tier to snake_case (e.g., 1.0 -> "1_0", 2.5 -> "2_5")
  const tierToSnakeCase = (tier: string | number): string => {
    const tierNum = parseFloat(String(tier));
    // Use toFixed(1) to ensure one decimal place, then remove trailing zero if it's a whole number
    // But keep the decimal format (e.g., 1.0 stays as "1.0", not "1")
    const tierStr = tierNum % 1 === 0 ? tierNum.toFixed(1) : tierNum.toString();
    // Replace dot with underscore
    return tierStr.replace('.', '_');
  };

  // Filter products based on billing period (only for tier >= 2.0, tier < 2.0 always shown)
  const filteredProducts = useMemo(() => {
    if (!canvaAuthenticated) {
      return [];
    }
    
    const userProfile = canvaAuthenticated.userProfile;
    const isPricingTest = userProfile.pricingTest === true;
    
    const base = !isPricingTest
      ? userProfile.products
      : userProfile.products.filter((product: any) => {
          const productTier = parseFloat(product.tier);
          // Always show products with tier < 2.0
          if (productTier < 2.0) {
            return true;
          }
          // For tier >= 2.0, filter based on billing period
          // billing field should be 'month' for monthly or 'year' for annual
          if (billingPeriod === 'monthly') {
            return product.billing === 'month';
          } else if (billingPeriod === 'annual') {
            return product.billing === 'year';
          }
          return false;
        });

    if (TEMP_HIDE_FREE_PRODUCT) {
      return base.filter((product: any) => !isFreeTierProduct(product));
    }
    return base;
  }, [canvaAuthenticated, billingPeriod]);
  
  if (!canvaAuthenticated) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5 w-100">
        <Spinner animation="border" role="status" className="canva-route-loading-spinner">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }
  
  const userProfile = canvaAuthenticated.userProfile;
  const env = canvaAuthenticated.env;
  const isPricingTest = userProfile.pricingTest === true;

  const onCancel = () => {
    // Navigation handled elsewhere
  };

const handleProductAction = async (product) => {

		setIsLoading(true);
		const priceId=product.stripePriceId;
		const keyTypeId=product.apiKeyTypeId;
		const productId=product.id;
		  const subscriptionId=userProfile.activeProduct.subscriptionId;
		  
		  // Determine if this is an upgrade or downgrade
		  const productTier = parseFloat(product.tier);
		  const activeTier = parseFloat(userProfile.activeProduct.tier);
		  const isTierUpgrade = productTier > activeTier;
		  
		  // If tiers are equal, check billing period: monthly -> annual is an upgrade
		  const isBillingUpgrade = productTier === activeTier && 
		    userProfile.activeProduct.billing === 'month' && 
		    product.billing === 'year';
		  
		  const isUpgrade = !subscriptionId || isTierUpgrade || isBillingUpgrade;
		  
		  if (isUpgrade) {
			// New purchase or subscription upgrade
			
			// Track InitiateCheckout event for Meta Pixel
			if (typeof window !== 'undefined' && (window as any).fbq) {
			  (window as any).fbq('track', 'InitiateCheckout', {
				content_name: product.name,
				content_ids: [product.id],
				value: product.price === 'free' ? 0 : parseFloat(product.price),
				currency: product.currency || 'USD'
			  });
			}
			
			// Store purchase info in localStorage for success page tracking
			if (typeof window !== 'undefined' && window.localStorage) {
			  const purchaseData = {
				productName: product.name,
				productId: product.id,
				value: product.price === 'free' ? 0 : parseFloat(product.price),
				currency: product.currency || 'USD'
			  };
			  window.localStorage.setItem('pendingPurchase', JSON.stringify(purchaseData));
			}
			
			try {
				let url = env.stripeSessionUrl;
			  const response = await fetch(url, {
				method: 'POST',
				headers: {
				  'Authorization': `Bearer ${token}`,
				  'Content-Type': 'application/json',
				},
				body: JSON.stringify({ priceId, productId,keyTypeId, subscriptionId }),
			  });
		
			  const session = await response.json();
			
			  if (session.url) {
				window.location.href=session.url;

			  } else {
				setError(env.strings['app-error-session-expired'] || 'app-error-session-expired');
			  }
			} catch (error: any) {
			  setError(`Failed: ${error.message}`);
			} finally {
			 // setLoadingButtons(false); // Clear loading state for all buttons
			}
		  } else {
			// Update existing subscription (downgrade)
			try {
		
				let url = env.stripeDowngradeUrl;
			  const response = await fetch(url, {
				method: 'POST',
				headers: {
				  'Authorization': `Bearer ${token}`,
				  'Content-Type': 'application/json',
				},
				body: JSON.stringify({ priceId, productId,keyTypeId,subscriptionId }),
			  });
		
			  const result = await response.json();
			  if (result.error) {
				setError('Error updating subscription');
			  } else {
				window.close();
			  }
			} catch (error: any) {
			  	setError(`Failed to downgrade: ${error.message}`);
			} finally {
			 
			} 
		  }
		};

  

  const handleDowngradeConfirmation = (product) => {
    handleProductAction(product);
    setDowngradeFlyout(false);
  };

  const handleDowngradeClick = (product) => {
    // Handle downgrade click (if needed)
  };

  const singleLineInfoLabel = (node: React.ReactNode) => (
    <span className="product-btn__single-line-label">{node}</span>
  );

  const getButton = (product) => {
  
    // Determine button properties dynamically
    let onClick: React.MouseEventHandler<HTMLButtonElement>;
    let variant: string = 'primary';
    let disabled: boolean = isLoading || userProfile.activeProduct.expires;
    let label: React.ReactNode = isLoading ?  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : (env.strings['app-product-list-button-label-upgrade'] || 'app-product-list-button-label-upgrade');
    let productBtnModifier: string | null = null;
  
    const productTier = parseFloat(product.tier);
    const activeTier = parseFloat(userProfile.activeProduct.tier);
    const isFreePlan = product.price === "free" || productTier <= 1.0;
  
    if (product.id === userProfile.activeProduct.id) {
      // Your plan - check expires first, then renewDate
      disabled = true;
      
      // For tier 1.0 products, use outline-secondary (tertiary) instead of primary/warning
      if (Math.abs(productTier - 1.0) < 0.01) {
        variant = 'outline-secondary';
        // Still show renewal/expiration info if available
        if (userProfile.activeProduct.expires) {
          productBtnModifier = 'product-btn--info-expires';
          const expiresDate = userProfile.activeProduct.expires.includes('T') 
            ? userProfile.activeProduct.expires.split('T')[0] 
            : userProfile.activeProduct.expires;
          label = singleLineInfoLabel(
            <>
              {env.strings['app-text-subscription-ends'] || 'app-text-subscription-ends'} {expiresDate}
            </>
          );
        } else if (userProfile.renewDate) {
          productBtnModifier = 'product-btn--info-renews';
          const renewDate = userProfile.renewDate.includes('T') 
            ? userProfile.renewDate.split('T')[0] 
            : userProfile.renewDate;
          label = singleLineInfoLabel(
            <>
              {env.strings['app-text-subscription-renews'] || 'app-text-subscription-renews'} {renewDate}
            </>
          );
        } else {
          label = env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan';
        }
      } else if (Math.abs(productTier - 1.9) < 0.01) {
        // For tier 1.9 products (not renewable), use subscription-ends instead of subscription-renews
        // Check expires first - use warning/danger color
        if (userProfile.activeProduct.expires) {
          variant = 'outline-secondary';
          productBtnModifier = 'product-btn--info-expires';
          const expiresDate = userProfile.activeProduct.expires.includes('T') 
            ? userProfile.activeProduct.expires.split('T')[0] 
            : userProfile.activeProduct.expires;
          label = singleLineInfoLabel(
            <>
              {env.strings['app-text-subscription-ends'] || 'app-text-subscription-ends'} {expiresDate}
            </>
          );
        } else if (userProfile.renewDate) {
          // For tier 1.9, use subscription-ends instead of subscription-renews
          variant = 'outline-secondary';
          productBtnModifier = 'product-btn--info-renews';
          const renewDate = userProfile.renewDate.includes('T') 
            ? userProfile.renewDate.split('T')[0] 
            : userProfile.renewDate;
          label = singleLineInfoLabel(
            <>
              {env.strings['app-text-subscription-ends'] || 'app-text-subscription-ends'} {renewDate}
            </>
          );
        } else {
          // Default - no renewal/expiration info
          variant = 'primary';
          label = env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan';
        }
      } else {
        // Check expires first - use warning/danger color
        if (userProfile.activeProduct.expires) {
          variant = 'outline-secondary';
          productBtnModifier = 'product-btn--info-expires';
          const expiresDate = userProfile.activeProduct.expires.includes('T') 
            ? userProfile.activeProduct.expires.split('T')[0] 
            : userProfile.activeProduct.expires;
          label = singleLineInfoLabel(
            <>
              {env.strings['app-text-subscription-ends'] || 'app-text-subscription-ends'} {expiresDate}
            </>
          );
        } else if (userProfile.renewDate) {
          variant = 'outline-secondary';
          productBtnModifier = 'product-btn--info-renews';
          const renewDate = userProfile.renewDate.includes('T') 
            ? userProfile.renewDate.split('T')[0] 
            : userProfile.renewDate;
          label = singleLineInfoLabel(
            <>
              {env.strings['app-text-subscription-renews'] || 'app-text-subscription-renews'} {renewDate}
            </>
          );
        } else {
          // Default - no renewal/expiration info
          variant = 'primary';
          label = env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan';
        }
      }
      
      onClick = () => {}; // No-op for current plan
    } else if (productTier < activeTier || (productTier === activeTier && userProfile.activeProduct.billing === 'year' && product.billing === 'month')) {
      // Downgrade option - button disabled if active tier < 2.0
      // Also treat same-tier annual -> monthly as downgrade
      variant = 'outline-secondary'; // Tertiary style (outline-secondary in Bootstrap)
      productBtnModifier = 'product-btn--downgrade';
      disabled = activeTier < 2.0 || disabled;
      onClick = activeTier >= 2.0 ? () => { setDowngradeFlyout(product); } : () => {};
      label = isLoading ? (env.strings['app-product-list-button-label-loading'] || 'app-product-list-button-label-loading') : (env.strings['app-product-list-button-label-downgrade'] || 'app-product-list-button-label-downgrade');
    } else if (isFreePlan && activeTier <= 1.0) {
      // If user is on free plan and looking at free plan (different product), disable it
      variant = 'secondary';
      disabled = true;
      label = (env.strings['app-product-list-button-label-your-plan'] || 'app-product-list-button-label-your-plan');
      onClick = () => {}; // No-op for free plan when already on free
    } else {
      // For tier 1.0 products, use outline-secondary (tertiary) instead of primary
      if (Math.abs(productTier - 1.0) < 0.01) {
        variant = 'outline-secondary';
      }
      onClick = () => { handleProductAction(product); };
    }
  
    // Render the button
    const buttonClassName = [
      'w-100',
      'rounded-4',
      'product-btn',
      product.id === userProfile.activeProduct.id ? 'btn-your-plan' : '',
      productBtnModifier || '',
    ]
      .filter(Boolean)
      .join(' ');
    
    return (
      <Button 
        className={buttonClassName}
        variant={variant}
        onClick={onClick}
        disabled={disabled}
      >
        {label}
      </Button>
    );
  };


  const renderContent = () => {
    return (
      <>
      	<div className="canva-pricing-cards-row">
      		{error && (
              <div className="canva-pricing-cards-row__error">
                <Alert variant="danger">{error}</Alert>
              </div>
            )}
          {
          filteredProducts.map((product, index) => {
            const tierSnakeCase = tierToSnakeCase(product.tier);
            const headlineStringKey = `app-product-list-tier_${tierSnakeCase}-headline`;
            const footerStringKey = `app-product-list-tier_${tierSnakeCase}-footer`;
            
            const productTierForCard = parseFloat(product.tier);
            const isFreeTier =
              productTierForCard === 1.0 || product.price === 'free';
            const isCurrentPlan = userProfile.activeProduct.id === product.id;
            const cardClasses = [
              'product-card',
              'canva-product-card',
              'd-flex',
              'flex-column',
              'h-100',
              'border',
              'position-relative',
              'text-start',
              'rounded-4',
              isCurrentPlan ? 'canva-product-card--current' : '',
              isFreeTier
                ? 'product-card-tier-1-0 canva-product-card--tier-free'
                : 'canva-product-card--paid',
            ]
              .filter(Boolean)
              .join(' ');
            
            return (
              <div className="canva-pricing-card-cell" key={product.id || `product-${index}`}>
                <div className={cardClasses}>
                  {isCurrentPlan && (
                    <Badge
                      className={
                        isFreeTier
                          ? 'canva-product-card-badge canva-product-card-badge--free'
                          : 'canva-product-card-badge canva-product-card-badge--super'
                      }
                    >
                      {isFreeTier
                        ? env.strings['app-product-badge-your-plan-free'] ||
                          env.strings['app-product-list-button-label-your-plan'] ||
                          'app-product-list-button-label-your-plan'
                        : env.strings['app-product-badge-your-plan-super'] ||
                          env.strings['app-product-list-button-label-your-plan'] ||
                          'app-product-list-button-label-your-plan'}
                    </Badge>
                  )}
              
                  <h4 className="product-name h4 fw-bold">
                    {isFreeTier ? (
                      <span className="canva-product-name-muted">{product.name || 'app-product-name-unknown'}</span>
                    ) : (
                      <span className="canva-gradient-text">{product.name || 'app-product-name-unknown'}</span>
                    )}
                  </h4>
                  
                  {isPricingTest && !HIDE_PRODUCT_DESCRIPTION_AND_POST_CTA && (
                    <div
                      className="product-headline canva-product-headline small mb-4"
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {env.strings[headlineStringKey] || ''}
                    </div>
                  )}
              
                  <div className="product-price canva-product-price-block mb-3">
                    <p className="mb-0">
                      <span className="canva-product-price-amount fw-bold">
                        {product.price === 'free'
                          ? env.strings['app-price-free'] || 'app-price-free'
                          : product.price}
                      </span>
                      {product.currency ? (
                        <span className="canva-product-price-currency fw-bold">
                          {'\u00A0'}
                          {product.currency}
                        </span>
                      ) : null}
                    </p>
                    <div className="canva-product-price-period small">
                      {(() => {
                        const productTier = parseFloat(product.tier);
                        if (productTier === 1.9) {
                          return env.strings['app-product-list-price-pay-once'] || 'app-product-list-price-pay-once';
                        }
                        if (productTier === 1.0) {
                          return env.strings['app-product-list-price-each-month'] ||
                            'app-product-list-price-each-month';
                        }
                        return product.billing === 'year'
                          ? env.strings['app-product-list-price-per-year'] ||
                              'app-product-list-price-per-year'
                          : env.strings['app-product-list-price-per-month'] ||
                              'app-product-list-price-per-month';
                      })()}
                    </div>
                  </div>
              
                  <ul className="product-features canva-product-features list-unstyled small mb-4 flex-grow-1 ps-0">
                    {product.features.map((feature, featureIndex) => {
                      const stringKey = `product-feature-${feature.featureKey}-description`;
                      const isBold = featureIndex < 2;
                      return (
                        <li key={`feature-${featureIndex}`} className="canva-product-feature-item mb-2 d-flex align-items-center gap-2">
                          <i className="bi bi-check-lg canva-product-feature-check flex-shrink-0" aria-hidden />
                          <span className={isBold ? 'fw-semibold' : ''}>{env.strings[stringKey] || stringKey}</span>
                        </li>
                      );
                    })}
                  </ul>
              
                  {/* Product Button */}
                  <div className="mt-auto">
                    {getButton(product)}
                  </div>
                  
                  {isPricingTest &&
                    !HIDE_PRODUCT_DESCRIPTION_AND_POST_CTA &&
                    env.strings[footerStringKey] && (
                    <div className="product-footer mt-2" style={{ fontStyle: 'italic' }}>
                      {env.strings[footerStringKey]}
                    </div>
                  )}
                </div>
              </div>
            );
          })
          }
      </div>
          <DowngradeConfirmationModal
			show={downgradeFlyout}
			product={downgradeFlyout}
			onHide={() => setDowngradeFlyout(false)}
			onConfirm={handleDowngradeConfirmation}
		  />
      </>
    );
  };


  return <>{renderContent()}</>;
};

export default ProductList;
